import { defineStore } from "pinia";
import { hasKey } from "@/functions/common";
import {
  getWebform,
  getListlist,
  getCampaignlist,
  getContactProperty,
  getTimezone,
  fetchTags,
  fetchContacts,
  getSegmentlist,
  fetchSender,
  fetchTrackingCodes,
  fetchAutomationListForDropdown,
  fetchCountryList,
  fetchTaxList,
  getAccountPreferences,
  fetchCurrentTime,
} from "@/api/common";
import { formatSelectOption } from "@/functions/common";

export const common = defineStore("common", {
  state: () => ({
    tagLookup: [],
    webformLookup: [],
    contactCount: 0,
    listLookup: [],
    listWithEncId: [],
    segmentLookup: [],
    campaignList: [],
    contactProperty: [],
    campaignTags: [],
    automations: [],
    timezone: [],
    contactList: [],
    senderIds: [],
    replyIds: [],
    defaultPermRemind: "",
    allowMultipleSender: false,
    allowAmpEmail: false,
    trackingCodes: [],
    senderLoaded: false,
    trackingLoaded: false,
    countryList: [],
    taxList: [],
    frequencyCapSettings: {},
    totalListCount: 0,
    allowDuplicateContacts: 0,
  }),
  getters: {
    campaignDropdown(state) {
      return state.campaignList.map(({ id, label, lastUpdated }) => ({
        id: id + "",
        label,
        lastUpdated,
      }));
    },
  },
  actions: {
    getWebform(params) {
      return getWebform(params).then((response) => {
        if (response.data?.data) {
          this.webformLookup = formatSelectOption(
            response.data.data,
            "name",
            "id"
          );
        }
      });
    },

    getTag(params) {
      return fetchTags(params).then((response) => {
        if (response.data?.tags) {
          this.tagLookup = formatSelectOption(
            response.data.tags,
            "label",
            "value"
          );
        }
      });
    },

    getListLookup(params, loadMore = false) {
      return getListlist(params).then((response) => {
        if (hasKey(response.data, "allowDuplicateContacts")) {
          this.allowDuplicateContacts = response.data.allowDuplicateContacts;
        }
        if (response.data?.data) {
          if (params.encryptId) {
            this.listWithEncId = formatSelectOption(
              response.data.data,
              "listname",
              "id"
            );
          } else {
            if (!params.search_val) {
              this.contactCount = response.data.all_contact_count;
            }

            // Store total list count for pagination
            this.totalListCount = response.data.count || 0;

            const formattedLists = formatSelectOption(
              response.data.data,
              "listname",
              "id"
            );

            // Append to existing list when loading more
            if (loadMore) {
              this.listLookup = [...this.listLookup, ...formattedLists];
            } else {
              this.listLookup = formattedLists;
            }
          }
        }
      });
    },

    getSegmentlist(params) {
      return getSegmentlist(params).then((response) => {
        if (response.data?.segment) {
          this.segmentLookup = formatSelectOption(
            response.data.segment,
            "name",
            "id"
          );
        }
      });
    },

    getCampaignlist(params) {
      return getCampaignlist(params).then((response) => {
        if (response.data?.data) {
          this.campaignList = formatSelectOption(
            response.data.data,
            "campName",
            "id",
            "lastUpdated"
          );
        }
        return response.data.count;
      });
    },

    getCampaignTags(params) {
      return fetchTags(params).then((response) => {
        this.campaignTags = response.data.tags.map(({ label, value }) => ({
          label,
          id: value,
        }));
      });
    },

    getAutomationList(params) {
      return fetchAutomationListForDropdown(params).then((response) => {
        this.automations = response.data.map(({ name, id, modified_date }) => ({
          label: name,
          id,
          lastUpdated: modified_date || "",
        }));
      });
    },

    fetchSender(params) {
      if (!this.senderLoaded) {
        this.senderLoaded = true;
        return fetchSender(params).then(({ data }) => {
          this.senderIds = data.sender;
          this.replyIds = data.reply;
          this.defaultPermRemind = data.permRemind;
          this.allowMultipleSender = data.allowMultipleSender;
          this.allowAmpEmail = data.allowAmpEmail;
          return data.verified;
        });
      }
    },

    getContactProperty(params) {
      return getContactProperty(params).then((response) => {
        if (response.data) {
          this.contactProperty = response.data;
        }
      });
    },

    fetchContacts(params) {
      return fetchContacts(params).then((response) => {
        if (response.data) {
          this.contactList = response.data;
        }
      });
    },

    getTimezone() {
      return getTimezone().then((response) => {
        if (response.data?.timezones) {
          this.timezone = response.data.timezones.map((item) => {
            return {
              id: item.gmt_zone,
              label: "(GMT" + item.gmt_adjust + ")" + item.gmt_zone,
              zone: item.gmt_zone,
            };
          });
        }
      });
    },

    fetchTrackingCodes() {
      if (!this.trackingLoaded) {
        this.trackingLoaded = true;
        fetchTrackingCodes().then((response) => {
          this.trackingCodes = response.data.conversionTrackingCodes;
        });
      }
    },
    fetchCountryList() {
      fetchCountryList().then((response) => {
        this.countryList = response.data.country_list.map((item) => {
          return {
            value: item.countryId,
            label: item.name,
          };
        });
      });
    },

    fetchTaxList() {
      fetchTaxList().then((response) => {
        this.taxList = response.data.data.map((item) => {
          return {
            value: item.taxType,
            label: item.taxName,
            format: item.format,
            countryName: item.country,
            countryCode: item.countryCode,
          };
        });
      });
    },
    getAccountPreferences() {
      getAccountPreferences().then((response) => {
        if (response.data) {
          if (response.data?.FrequencyCap) {
            if (
              response.data.FrequencyCap.frequency == 0 ||
              response.data.FrequencyCap.time_period == 0
            ) {
              this.frequencyCapSettings = {};
            } else {
              this.frequencyCapSettings = response.data.FrequencyCap;
            }
          }
          this.allowDuplicateContacts =
            response.data?.allowDuplicateContacts === 1 ? 1 : 0;
        }
      });
    },
    reloadSenderIds(params) {
      this.senderLoaded = false;
      return this.fetchSender(params);
    },

    fetchCurrentTime(id) {
      return fetchCurrentTime(id);
    },
  },
});
